import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { mockNotifications, mockNotificationRecipients } from '../../mock';

interface NotificationRecipientsPageProps {
  notificationId: string;
  onBack: () => void;
}

export function NotificationRecipientsPage({ notificationId, onBack }: NotificationRecipientsPageProps) {
  const [recipients] = useState(mockNotificationRecipients);
  const notification = mockNotifications.find(n => n.id === notificationId);

  const notificationRecipients = recipients.filter(r => r.notificationId === notificationId);

  if (!notification) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 3 }}>
          Back to Notifications
        </Button>
        <Typography variant="h6" color="text.secondary">
          Notification not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 3 }}
      >
        Back to Notifications
      </Button>

      <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
        Notification Recipients
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {notification.title}
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h4" fontWeight={600} color="primary">
              {notificationRecipients.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Recipients
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h4" fontWeight={600} color="success.main">
              {notificationRecipients.filter(r => r.readAt).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Read
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h4" fontWeight={600} color="text.secondary">
              {notificationRecipients.filter(r => !r.readAt).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unread
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Recipient ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Read At</TableCell>
                <TableCell>Email Sent At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notificationRecipients.map((recipient) => (
                <TableRow key={recipient.id}>
                  <TableCell>{recipient.userId}</TableCell>
                  <TableCell>
                    <Chip
                      label={recipient.userType.charAt(0).toUpperCase() + recipient.userType.slice(1)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{recipient.email}</TableCell>
                  <TableCell>
                    {recipient.readAt ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Read"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<CircleIcon />}
                        label="Unread"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {recipient.readAt
                      ? new Date(recipient.readAt).toLocaleString()
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    {recipient.emailSentAt
                      ? new Date(recipient.emailSentAt).toLocaleString()
                      : '-'
                    }
                  </TableCell>
                </TableRow>
              ))}
              {notificationRecipients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No recipients found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
